import { DEFAULT_DASHBOARD_LAYOUT } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { LayoutGrid, ShieldAlert } from 'lucide-react';
import { LayoutRenderer } from '@/components/customisation/render/layout-renderer';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { publicDashboardQuery } from '@/lib/customisation/queries';
import { useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/overview')({ component: Overview });

/**
 * The customer overview: the tenant's published dashboard layout (or Hearth's default when
 * nothing is published or it can't be loaded), rendered from the component registry. The
 * welcome and the two-factor nudge sit above it and aren't part of the layout.
 */
function Overview() {
  const { user } = useAuthenticatedSession();
  const dashboard = useQuery(publicDashboardQuery());
  return (
    <>
      <PageHeader
        title="Overview"
        icon={LayoutGrid}
        description={`Welcome back, ${user.username}. Your servers and account at a glance.`}
      />
      <div className="grid gap-8">
        {!user.twoFactorEnabled && (
          <Card className="flex-row flex-wrap items-center gap-3 px-4 py-3">
            <ShieldAlert className="text-warning size-5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 text-sm">
              <span className="font-medium">Two-factor authentication is off.</span>{' '}
              <span className="text-muted-foreground">
                Turn it on so a leaked password isn’t enough to reach your servers.
              </span>
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to="/account/security">Set it up</Link>
            </Button>
          </Card>
        )}
        {dashboard.isPending ? (
          <div className="grid gap-3" aria-busy>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
            <Skeleton className="h-40 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        ) : (
          <LayoutRenderer
            layout={dashboard.data?.layout ?? DEFAULT_DASHBOARD_LAYOUT}
            mode="live"
            className="@container grid gap-8"
          />
        )}
      </div>
    </>
  );
}
